/**
 * Which plane each node type belongs on, derived from the references rather than declared.
 *
 * The layered layout needs an order over types, and an earlier draft hardcoded one - four names in
 * a fixed sequence. That works for exactly one dataset. A host's types are whatever its model says,
 * so the order has to come out of the data, and the only thing in the data that says a type sits
 * "above" another is the direction of the references between them.
 *
 * So: aggregate every reference into a type-level digraph, break its cycles, and layer it by
 * longest path. A type that is only ever referenced ends up at the top, a type that only ever
 * references ends up at the bottom, and the planes between them are ordered by how far along the
 * reference chains they sit.
 *
 * What this deliberately does *not* do is encode a drawing convention. An architect draws
 * technology beneath the applications that run on it, but the `runs-on` reference points from the
 * application to the technology - so convention and reference direction disagree, and the layout
 * cannot tell which is meant without reading reference type names it has no business interpreting.
 * Reference direction is the honest answer and it is the one a reader can verify from the edges on
 * screen.
 */

import type { Link } from '../graph/topology';
import type { NetworkNode } from '../types';

export type TypeLayering = {
  /** Layer per type name. 0 is the bottom plane. */
  layerOf: Map<string, number>;
  /** Layer per node, parallel to the node list handed in. */
  layerIndices: number[];
  /** How many distinct planes were assigned. At least 1 whenever there are any nodes. */
  layerCount: number;
};

/**
 * Deterministic, locale-independent name order.
 *
 * Codepoint order rather than `localeCompare`, and that is the point rather than a way around the
 * lint rule: a locale-aware comparison would order two type names differently for a German reader
 * than for an English one, so the same estate would come out with its colors and its planes
 * swapped depending on who opened it. Ties here exist only to be broken reproducibly.
 */
const compareNames = (left: string, right: string): number =>
  left < right ? -1 : left > right ? 1 : 0;

type TypeEdge = {
  from: string;
  to: string;
  weight: number;
};

/**
 * Aggregates the node-level references into a weighted digraph over type names.
 *
 * Nested maps rather than one map keyed on a joined string. Type names are the host's and can
 * contain anything, so no separator is provably safe to join on - with a space, `"A B" -> "C"` and
 * `"A" -> "B C"` collide - and the NUL byte that would be safe is an invisible control character
 * sitting in a source file, which is its own kind of trap. Two levels of map needs no separator.
 */
const toTypeEdges = (
  nodes: readonly NetworkNode[],
  links: readonly Link[]
): TypeEdge[] => {
  const weights = new Map<string, Map<string, number>>();

  for (const { source, target } of links) {
    const from = nodes[source]?.typeName;
    const to = nodes[target]?.typeName;
    /* A reference within one type says nothing about ordering and would be a self-loop in the
       type graph, which no layering can satisfy. */
    if (from === undefined || to === undefined || from === to) continue;

    const outgoing = weights.get(from) ?? new Map<string, number>();
    outgoing.set(to, (outgoing.get(to) ?? 0) + 1);
    weights.set(from, outgoing);
  }

  return [...weights.entries()].flatMap(([from, outgoing]) =>
    [...outgoing.entries()].map(([to, weight]) => ({ from, to, weight }))
  );
};

/**
 * Whether adding `from -> to` would close a cycle, by asking if `from` is already reachable
 * from `to`.
 */
const reaches = (
  adjacency: Map<string, Set<string>>,
  start: string,
  goal: string
): boolean => {
  const stack = [start];
  const seen = new Set<string>();

  while (stack.length > 0) {
    const at = stack.pop();
    if (at === undefined || seen.has(at)) continue;
    if (at === goal) return true;
    seen.add(at);

    for (const next of adjacency.get(at) ?? []) stack.push(next);
  }

  return false;
};

/**
 * The heaviest acyclic subset of the type edges, taken greedily.
 *
 * Cycles are the normal case rather than the exotic one: applications reference each other's
 * types in both directions somewhere in any real estate, and one such pair makes the whole graph
 * unlayerable. Dropping the lighter direction keeps the ordering that the greater number of
 * references actually supports, which is the same principle Sugiyama layering uses - and the
 * dropped edges are still drawn, they just do not get a say in the plane order.
 *
 * Greedy rather than optimal. Minimum feedback arc set is NP-hard, the input here is a graph over
 * *types* - tens of nodes, not thousands - and the cost of a suboptimal choice is one plane
 * boundary a reader might have drawn differently.
 */
const toAcyclicEdges = (edges: readonly TypeEdge[]): TypeEdge[] => {
  const byWeight = [...edges].sort(
    (left, right) =>
      right.weight - left.weight ||
      /* Tie-broken by name so the layering is deterministic: two type pairs with equal reference
         counts must not swap planes between runs, or a Chromatic snapshot never settles. */
      compareNames(left.from, right.from) ||
      compareNames(left.to, right.to)
  );

  const adjacency = new Map<string, Set<string>>();
  const kept: TypeEdge[] = [];

  for (const edge of byWeight) {
    if (reaches(adjacency, edge.to, edge.from)) continue;

    kept.push(edge);
    const outgoing = adjacency.get(edge.from) ?? new Set<string>();
    outgoing.add(edge.to);
    adjacency.set(edge.from, outgoing);
  }

  return kept;
};

export const toTypeLayering = (
  nodes: readonly NetworkNode[],
  links: readonly Link[]
): TypeLayering => {
  const typeNames = [...new Set(nodes.map(({ typeName }) => typeName))].sort();
  const kept = toAcyclicEdges(toTypeEdges(nodes, links));

  const predecessors = new Map<string, string[]>();
  for (const { from, to } of kept) {
    predecessors.set(to, [...(predecessors.get(to) ?? []), from]);
  }

  /**
   * Longest path to each type, memoised.
   *
   * Recursion terminates because `kept` is acyclic by construction; the `resolving` guard is a
   * belt-and-braces stop rather than an expected path, since a bug in the cycle breaking would
   * otherwise recurse until the stack gave out.
   */
  const layerOf = new Map<string, number>();
  const resolving = new Set<string>();

  const layerFor = (typeName: string): number => {
    const known = layerOf.get(typeName);
    if (known !== undefined) return known;
    if (resolving.has(typeName)) return 0;

    resolving.add(typeName);
    const incoming = predecessors.get(typeName) ?? [];
    const layer =
      incoming.length === 0
        ? 0
        : 1 + Math.max(...incoming.map(from => layerFor(from)));
    resolving.delete(typeName);

    layerOf.set(typeName, layer);
    return layer;
  };

  for (const typeName of typeNames) layerFor(typeName);

  return {
    layerOf,
    layerIndices: nodes.map(({ typeName }) => layerOf.get(typeName) ?? 0),
    layerCount: typeNames.length === 0 ? 0 : Math.max(...layerOf.values()) + 1,
  };
};
